import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, BarChart3, Compass, MapPin, SearchCheck, ShieldCheck, Target, TrendingUp } from "lucide-react";
import { Asterisk } from "@/components/Asterisk";
import { PageHero } from "@/components/PageHero";
import { CtaBand } from "@/components/sections/CtaBand";
import { RelatedNews } from "@/components/sections/RelatedNews";
import { ClientTestimonial } from "@/components/sections/ClientTestimonial";
import { ContactForm } from "@/components/ContactForm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const PAGE_TITLE = "About Phil Carr: SEO Consultant Barnsley";
const PAGE_DESCRIPTION =
  "Meet Phil Carr, Barnsley SEO consultant with 12+ years' experience in technical SEO, content strategy, web design and enquiry-led growth.";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESCRIPTION },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESCRIPTION },
      { property: "og:url", content: "https://www.phil-carr.co.uk/about" },
      {
        property: "og:image",
        content: "https://www.phil-carr.co.uk/images/generated/about-strategy.jpg",
      },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESCRIPTION },
      {
        name: "twitter:image",
        content: "https://www.phil-carr.co.uk/images/generated/about-strategy.jpg",
      },
    ],
    links: [{ rel: "canonical", href: "https://www.phil-carr.co.uk/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: AboutPage,
});

const agencyItems = [
  {
    icon: SearchCheck,
    title: "Results-driven SEO strategy",
    body: "I start by agreeing what a valuable enquiry or sale looks like for your business. Search visibility and traffic are reviewed alongside those outcomes, with tracking gaps made clear. That keeps the work connected to the services you want to grow.",
  },
  {
    icon: Target,
    title: "Tailored growth plans",
    body: "Your priorities depend on the website you have, the customers you serve and the resources available. I review those factors before recommending work, then explain which changes should come first and what your team needs to contribute.",
  },
  {
    icon: Compass,
    title: "Search intent research",
    body: "I research the questions and service searches your prospective customers use, then assess what they need from the page. The findings guide content structure and targeting, with buying intent and relevance considered alongside search volume.",
  },
];

const missionVision = [
  {
    label: "my mission",
    title: "Help businesses grow",
    body: "I help UK businesses make their services easier to find and understand online. That means practical work on the website, useful explanations for customers and a clear way to assess whether the changes are bringing better enquiries.",
  },
  {
    label: "my vision",
    title: "Trusted SEO partner",
    body: "I want clients to understand their website well enough to make informed decisions about it. Direct conversations, explainable recommendations and a manageable plan help build a working relationship that can develop as the business changes.",
  },
  {
    label: "my values",
    title: "Transparent and honest",
    body: "I explain what the evidence shows, what remains uncertain and what a recommendation involves before work begins. If a task needs input from your team or sits outside the agreed scope, I make that clear so you can plan around it.",
  },
];

const counters = [
  { value: "SEO", label: "and web design", body: "SEO and web design projects across the UK." },
  { value: "12+", label: "years experience", body: "Supporting brands with technical SEO and strategy." },
  { value: "95%", label: "client retention", body: "Long-term partnerships built on measurable outcomes." },
];

const benefitItems = [
  { icon: TrendingUp, title: "Cost-effective growth" },
  { icon: ShieldCheck, title: "Greater trust online" },
  { icon: BarChart3, title: "Competitive advantage" },
];

const whatWeDo = [
  {
    icon: MapPin,
    title: "Local SEO solutions",
    body: "Strengthen your presence in Barnsley and across Yorkshire with location-targeted search optimisation.",
  },
  {
    icon: SearchCheck,
    title: "Technical SEO",
    body: "Improve crawlability, speed, and site structure for long-term ranking gains.",
  },
];

const steps = [
  { n: "01", title: "discovery & audit", body: "I review your website, available search data and the pages competing for your main services. We also discuss the enquiries you want more of, so the audit reflects the business as well as its technical setup." },
  { n: "02", title: "strategy & roadmap", body: "I turn the findings into a sequence of practical tasks with clear reasons and responsibilities. We agree what happens first and how the work fits your budget, content approvals and development capacity." },
  { n: "03", title: "implementation", body: "I deliver the agreed website and content changes, or provide briefs and checks for your existing team. Completed work is reviewed on the live site so we can confirm that the intended changes are in place." },
  { n: "04", title: "reporting & growth", body: "I review performance against the starting position and discuss the quality of the enquiries received. The next priorities follow that evidence, with realistic expectations about what needs more time and what needs a different approach." },
];

const supportItems = [
  {
    icon: SearchCheck,
    title: "Technical SEO",
    body: "I handle audits, crawl issues, schema, and performance improvements.",
  },
  {
    icon: Compass,
    title: "Content strategy",
    body: "I plan service pages, support content, and internal linking around real search intent.",
  },
  {
    icon: BarChart3,
    title: "Reporting & priorities",
    body: "I keep the roadmap focused on visibility, lead quality, and the next highest-value fix.",
  },
];

const faqs = [
  {
    q: "What is your background and experience?",
    a: "I'm a Barnsley-based SEO consultant and web strategist with over a decade of experience helping businesses across Yorkshire and the UK improve their search visibility and grow organically. My background combines technical SEO expertise with practical business understanding, having worked with everything from local Barnsley businesses to enterprise-level clients.",
  },
  {
    q: "Why do you focus on Barnsley and Yorkshire?",
    a: "I'm based in Barnsley and understand the unique challenges and opportunities that local Yorkshire businesses face. Whether it's competing with Sheffield or Leeds, targeting local searches in Barnsley, Wakefield, or Doncaster, or supporting broader Yorkshire campaigns, I know what works in this market.",
  },
  {
    q: "What makes your approach different?",
    a: "I focus on strategy-led SEO that goes beyond quick fixes. Rather than chasing rankings alone, I work on building sustainable growth through better content, stronger technical foundations, and clearer user journeys.",
  },
  {
    q: "Do you work with businesses outside Yorkshire?",
    a: "Yes. While Barnsley and Yorkshire are my core focus, I support businesses across the UK. Many clients want local SEO support in their area while also building broader search visibility.",
  },
  {
    q: "How do you measure success?",
    a: "Success is measured by business outcomes, not just rankings. I track organic traffic growth, lead quality improvements, conversion rate changes, and how SEO contributes to your wider goals.",
  },
  {
    q: "Do you provide ongoing support or just one-off projects?",
    a: "Both. Some businesses need a one-off SEO audit or website project, while others benefit from ongoing monthly support. SEO works best as a continuous process — I can support either approach depending on your needs.",
  },
];

function AboutPage() {
  return (
    <>
      <PageHero title="SEO consultant Barnsley: about Phil Carr" />

      {/* INTRO — strategy-led SEO and web design that delivers */}
      <section className="container mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
        <div className="row g-5 align-items-center">
          <div className="col-lg-7">
            <p className="demo-section-label">about</p>
            <h2 className="site-heading-lg mt-4 font-display font-light">
              Strategy-led SEO and web design that <em>delivers</em>
            </h2>
          </div>
          <div className="col-lg-5">
            <p className="site-copy text-muted-foreground">
              I deliver measurable SEO and conversion-focused web design for businesses in
              Barnsley and across the UK. Every engagement is built on technical insight, clear
              reporting, and content that aligns to search intent.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-3 demo-button-primary px-7 py-4 text-sm font-semibold transition-transform hover:-translate-y-0.5"
            >
              Contact me
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="mt-12 row g-5 align-items-stretch">
          <div className="col-lg-6">
            <div className="relative h-full overflow-hidden rounded-[10px] border border-border">
              <img
                src="/images/brand/about-seo-portrait-1200.webp"
                alt="Search queries, website layouts and validation checks illustrating SEO and web design planning"
                title="SEO and web design planning"
                data-name="SEO consultant reviewing analytics dashboards"
                width={1200}
                height={1800}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="col-lg-6">
            <div className="d-grid gap-4">
              {agencyItems.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className={`group row g-4 border border-border bg-background/70 p-6 transition-colors hover:border-primary sm:p-8 ${
                      idx === 0 ? "border-primary" : ""
                    }`}
                  >
                    <div className="col-auto">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full border border-border bg-secondary/70 text-primary transition-colors group-hover:border-primary">
                        <Icon className="h-7 w-7" strokeWidth={1.5} />
                      </div>
                    </div>
                    <div className="col">
                      <h3 className="site-heading-md font-display">{item.title}</h3>
                      <p className="site-copy mt-3 text-muted-foreground">{item.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* MY APPROACH — mission/vision/values */}
      <section className="border-y border-border bg-card/80">
        <div className="container mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
          <div className="row g-5 align-items-end">
            <div className="col-lg-7">
              <p className="demo-section-label">my approach</p>
              <h2 className="site-heading-lg mt-4 font-display font-light">
                Clear strategy for sustainable <em>SEO growth</em>
              </h2>
            </div>
            <div className="col-lg-5">
              <p className="site-copy text-muted-foreground">
                I start every project with a technical audit, competitor research, and a roadmap
                that ties visibility to conversions. Sustainable growth, not vanity metrics.
              </p>
            </div>
          </div>

          <div className="mt-20 row g-4 lg:mt-16">
            {missionVision.map((item) => (
              <div key={item.label} className="col-md-6 col-lg-4">
                <div className="d-flex h-full flex-column border border-border bg-background p-8 transition-colors hover:border-primary sm:p-10">
                  <p className="demo-section-label">{item.label}</p>
                  <h3 className="site-heading-md mt-4 font-display">{item.title}</h3>
                  <p className="site-copy mt-4 text-muted-foreground">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE — counters + image */}
      <section className="border-y border-border bg-card/80">
        <div className="container mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
          <div className="row g-5 align-items-center">
            <div className="col-lg-8">
              <p className="demo-section-label">why choose me</p>
              <h2 className="site-heading-lg mt-4 font-display font-light max-w-3xl">
                Expert strategy with <em>transparent</em> reporting
              </h2>
            </div>
            <div className="col-lg-4 flex lg:justify-end">
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 demo-button-primary px-7 py-4 text-sm font-semibold transition-transform hover:-translate-y-0.5"
              >
                Contact me
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="mt-12 row g-4 align-items-stretch">
            <div className="col-lg-8">
              <div className="row g-4">
                {counters.map((c) => (
                  <div key={c.label} className="col-md-4">
                    <div className="d-flex h-full flex-column justify-between border border-border bg-card p-7 transition-colors hover:border-primary sm:p-8">
                      <p className="demo-section-label">{c.label}</p>
                      <h3 className="mt-6 font-display text-5xl font-light text-foreground sm:text-6xl">
                        {c.value}
                      </h3>
                      <p className="site-copy mt-4 text-sm text-muted-foreground">{c.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-lg-4">
              <div className="relative h-full min-h-[320px] overflow-hidden rounded-[10px] border border-border">
                <img
                  src="/images/brand/seo-strategy-1200.webp"
                  alt="Branded SEO strategy illustration with website checks and search analysis"
                  title="SEO strategy and website checks"
                  data-name="SEO strategy and website checks"
                  width={1280}
                  height={1280}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLIENT BENEFITS — image left + content right */}
      <section className="border-y border-border bg-card/80">
        <div className="container mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
          <div className="row g-5 align-items-center">
            <div className="col-lg-6">
              <div className="relative h-full min-h-[360px] overflow-hidden rounded-[10px] border border-border">
                <img
                  src="/images/brand/local-search-barnsley-1200.webp"
                  alt="Branded local SEO illustration for Barnsley search visibility"
                  title="Local SEO visibility for Barnsley businesses"
                  data-name="Local SEO visibility for Barnsley businesses"
                  width={1600}
                  height={1100}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="col-lg-6">
              <p className="demo-section-label">client benefits</p>
              <h2 className="site-heading-lg mt-4 font-display font-light">
                Stronger rankings, smarter <em>conversion</em>
              </h2>
              <p className="site-copy mt-6 text-muted-foreground">
                Sustainable growth means improving visibility, UX, and conversion rate together.
                Every recommendation ties back to revenue and lead quality.
              </p>
              <ul className="bootstrap-list-row mt-8 row -translate-x-[30px] list-none pl-0">
                {[
                  "Clear technical SEO roadmaps",
                  "Conversion-first content strategy",
                  "Higher-quality organic traffic",
                  "Stronger local visibility",
                ].map((item) => (
                  <li key={item} className="col-sm-6 flex items-start gap-3 pl-0">
                    <Asterisk className="mt-1 h-4 w-4 shrink-0 text-accent" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10 row g-3">
                {benefitItems.map((b) => {
                  const Icon = b.icon;
                  return (
                    <div key={b.title} className="col-md-4">
                      <div className="flex h-full items-center gap-4 border border-border bg-background p-4 transition-colors hover:border-primary">
                        <Icon className="h-6 w-6 shrink-0 text-primary" strokeWidth={1.5} />
                        <span className="font-display text-sm">{b.title}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT I DO */}
      <section className="container mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
        <div className="row g-5 align-items-center">
          <div className="col-lg-6">
            <p className="demo-section-label">what I do</p>
            <h2 className="site-heading-lg mt-4 font-display font-light">
              Delivering SEO that drives <em>growth</em>
            </h2>
            <p className="site-copy mt-6 text-muted-foreground">
              From technical fixes to content and conversion improvements, I focus on improving
              visibility and driving qualified enquiries.
            </p>

            <div className="mt-10 d-grid gap-4">
              {whatWeDo.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className="group row g-4 border border-border bg-background p-6 transition-colors hover:border-primary"
                  >
                    <div className="col-auto">
                      <div className="flex h-14 w-14 items-center justify-center rounded-full border border-border bg-secondary/70 text-primary group-hover:border-primary">
                        <Icon className="h-6 w-6" strokeWidth={1.5} />
                      </div>
                    </div>
                    <div className="col">
                      <h3 className="font-display text-xl">{item.title}</h3>
                      <p className="site-copy mt-2 text-sm text-muted-foreground">{item.body}</p>
                    </div>
                  </div>
                );
              })}

              <Link
                to="/contact"
                className="group row g-4 border border-primary bg-primary/10 p-6 transition-colors hover:bg-primary hover:text-primary-foreground"
              >
                <div className="col">
                  <h3 className="font-display text-xl">Let's build your growth plan</h3>
                  <p className="mt-2 text-sm text-muted-foreground group-hover:text-primary-foreground/90">
                    Ready to improve rankings and leads? Let's design a strategy tailored to your
                    goals and market.
                  </p>
                </div>
                <div className="col-auto self-center">
                  <ArrowUpRight className="h-6 w-6 transition-transform group-hover:rotate-45" />
                </div>
              </Link>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="relative h-full min-h-[420px] overflow-hidden rounded-[10px] border border-border">
              <img
                src="/images/brand/seo-strategy-1200.webp"
                alt="Branded SEO strategy illustration with search checks and content planning"
                title="SEO strategy planning"
                data-name="SEO strategy planning"
                width={1280}
                height={960}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 4 step */}
      <section className="border-y border-border bg-card/80">
        <div className="container mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
          <div className="row g-5 align-items-end">
            <div className="col-lg-7">
              <p className="demo-section-label">how it works</p>
              <h2 className="site-heading-lg mt-4 font-display font-light">
                Step-by-step process to <em>SEO success</em>
              </h2>
            </div>
            <div className="col-lg-5">
              <p className="site-copy text-muted-foreground">
                Each engagement follows a clear, proven workflow: audit, strategy, implementation,
                and reporting. You always know what’s being done and why.
              </p>
            </div>
          </div>

          <div className="mt-20 lg:mt-16">
            <div className="row g-4">
              {steps.map((s) => (
                <div key={s.n} className="col-md-6 col-lg-3">
                  <div className="d-grid min-h-[260px] grid-rows-[auto_auto_1fr] border border-border bg-background px-7 py-8 transition-colors hover:border-primary sm:px-8 sm:py-10">
                    <span className="font-display text-3xl text-muted-foreground">{s.n}</span>
                    <h3 className="mt-4 font-display text-xl">{s.title}</h3>
                    <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW I HELP — 3 support cards */}
      <section className="container mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
        <div className="row g-5 align-items-end">
          <div className="col-lg-7">
            <p className="demo-section-label">how I help</p>
            <h2 className="site-heading-lg mt-4 font-display font-light">
              Support built around your <em>growth</em>
            </h2>
          </div>
        </div>

        <div className="mt-10 row g-4">
          {supportItems.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.title} className="col-md-6 col-lg-4">
                <Link
                  to="/contact"
                  className="group d-flex h-full flex-column border border-border bg-background p-8 transition-colors hover:border-primary"
                >
                  <div className="flex items-center justify-between">
                    <Icon className="h-10 w-10 text-accent" strokeWidth={1.5} />
                    <ArrowUpRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:rotate-45 group-hover:text-primary" />
                  </div>
                  <h3 className="mt-8 font-display text-2xl">{item.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
                </Link>
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex justify-center lg:justify-end">
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 demo-button-primary px-7 py-4 text-sm font-semibold transition-transform hover:-translate-y-0.5"
          >
            Work with me
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <ClientTestimonial />

      {/* FAQs — homepage style */}
      <section className="demo-faq-section">
        <div className="container mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
          <div className="demo-faq-block">
            <div className="demo-section-heading">
              <h3>about faqs</h3>
              <h2>
                Common <span>questions</span>
              </h2>
            </div>
            <Accordion type="single" collapsible className="demo-accordion w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="demo-accordion-item">
                  <AccordionTrigger className="demo-accordion-trigger group relative flex w-full text-left font-display hover:no-underline [&>svg.lucide-chevron-down]:hidden">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="demo-accordion-content text-base leading-relaxed">
                    <p>{f.a}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <RelatedNews count={10} title="Latest news" eyebrow="From the blog" />

      <CtaBand />

      {/* CONTACT */}
      <section className="inner-contact-section bg-background/95">
        <div className="container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
          <div className="row g-5 align-items-start">
            <div className="col-lg-5">
              <p className="demo-section-label">Get in touch</p>
              <h2 className="site-heading-lg mt-4 font-display font-light">
                Start a <em>conversation</em>
              </h2>
              <p className="site-copy mt-6 text-muted-foreground">
                I'm here to help. Reach out for expert guidance, personalised SEO solutions, web
                design, web hosting or any questions you have.
              </p>
              <dl className="mt-8 space-y-4 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-widest text-muted-foreground">Phone</dt>
                  <dd className="mt-1">
                    <a href="tel:+441226697325" className="text-foreground underline-offset-4 hover:underline">
                      01226 697 325
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-widest text-muted-foreground">Email</dt>
                  <dd className="mt-1">
                    <a href="mailto:info@phil-carr.co.uk" className="text-foreground underline-offset-4 hover:underline">
                      info@phil-carr.co.uk
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-widest text-muted-foreground">Based in</dt>
                  <dd className="mt-1 text-foreground">Barnsley, South Yorkshire, UK</dd>
                </div>
              </dl>
            </div>
            <div className="col-lg-7">
              <ContactForm pageContext="About" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
